// Conversão de durações entre a unidade de armazenamento de cada campo
// (ms / segundos / minutos / dias) e os "segmentos" exibidos no input
// composto (dias · horas · minutos · segundos). Tudo passa por segundos.

export type DurationUnit = 'ms' | 'seconds' | 'minutes' | 'hours' | 'days'
export type DurationSegment = 'days' | 'hours' | 'minutes' | 'seconds'

const SECONDS_PER: Record<DurationUnit, number> = {
  ms: 1 / 1000,
  seconds: 1,
  minutes: 60,
  hours: 60 * 60,
  days: 60 * 60 * 24,
}

export type SegmentValues = Partial<Record<DurationSegment, number>>

/** valor na unidade de armazenamento -> total de segundos */
export function toSeconds(value: number, unit: DurationUnit): number {
  return value * SECONDS_PER[unit]
}

/** total de segundos -> valor na unidade de armazenamento */
export function fromSeconds(seconds: number, unit: DurationUnit): number {
  return seconds / SECONDS_PER[unit]
}

/** distribui um total de segundos nos segmentos pedidos (maior primeiro) */
export function splitDuration(
  totalSeconds: number,
  segments: DurationSegment[]
): SegmentValues {
  const ordered = [...segments].sort((a, b) => SECONDS_PER[b] - SECONDS_PER[a])
  let remaining = Math.max(0, Math.round(totalSeconds))
  const out: SegmentValues = {}
  ordered.forEach((seg, index) => {
    const unitSeconds = SECONDS_PER[seg]
    if (index === ordered.length - 1) {
      // o menor segmento absorve o resto
      out[seg] = Math.round(remaining / unitSeconds)
    } else {
      const amount = Math.floor(remaining / unitSeconds)
      out[seg] = amount
      remaining -= amount * unitSeconds
    }
  })
  return out
}

/** combina valores de segmentos -> total de segundos */
export function combineDuration(parts: SegmentValues): number {
  return (
    Object.entries(parts) as [DurationSegment, number | undefined][]
  ).reduce(
    (acc, [seg, value]) => acc + (Number(value) || 0) * SECONDS_PER[seg],
    0
  )
}

/** valor armazenado -> segmentos exibidos */
export function storeToSegments(
  value: number,
  storeUnit: DurationUnit,
  segments: DurationSegment[]
): SegmentValues {
  return splitDuration(toSeconds(value, storeUnit), segments)
}

/** segmentos exibidos -> valor armazenado (inteiro) */
export function segmentsToStore(
  parts: SegmentValues,
  storeUnit: DurationUnit
): number {
  return Math.round(fromSeconds(combineDuration(parts), storeUnit))
}

const SEGMENT_LONG: Record<DurationSegment, [string, string]> = {
  days: ['dia', 'dias'],
  hours: ['hora', 'horas'],
  minutes: ['minuto', 'minutos'],
  seconds: ['segundo', 'segundos'],
}

/** resumo legível de um valor (ex.: "1 dia", "1 hora e 30 minutos", "imediato") */
export function formatDuration(
  value: number | null | undefined,
  storeUnit: DurationUnit,
  segments: DurationSegment[]
): string {
  if (value == null) return '—'
  const parts = storeToSegments(value, storeUnit, segments)
  const ordered = [...segments].sort((a, b) => SECONDS_PER[b] - SECONDS_PER[a])
  const pieces = ordered
    .map((seg) => {
      const amount = parts[seg] ?? 0
      if (!amount) return null
      const [one, many] = SEGMENT_LONG[seg]
      return `${amount} ${amount === 1 ? one : many}`
    })
    .filter((piece): piece is string => piece !== null)
  if (pieces.length === 0) return 'imediato'
  if (pieces.length === 1) return pieces[0] ?? 'imediato'
  return `${pieces.slice(0, -1).join(', ')} e ${pieces[pieces.length - 1]}`
}
